import dom from './util/dom';
import Attr from './Attr';

/** @param {string} name */
const getHelpBlockId = name => `tn-bridge-help-block-${name}`;


/**
 * @param {BridgeFormControl} control
 */
function getIsRequired({ $control, $group }) {
  if ($control.getAttribute(Attr.Data.Tn.Required) === 'true') {
    return true;
  }

  return !!($group && dom.findFrom($group, '.tn-required-field'));
}


class BridgeFormControl {
  /**
   * @param {Element} $group
   */
  constructor($group) {
    /** @type {HTMLInputElement} */
    const $control = dom.findFrom($group, 'input,select,textarea');

    this.$group = $group;
    this.$control = $control;
    this.name = $control.name;
    this.type = ($control.type || $control.tagName).toLowerCase();

    if (!this.$group) {
      throw new Error('missing group, non-hidden');
    }

    this.initObservers();

    this.groupObserver.observe(this.$group, {
      attributes: true,
      attributeOldValue: true,
      attributeFilter: ['class'],
    });

    this.$control.removeAttribute(Attr.Aria.Required);

    this.fieldRequiredChangedObserver.observe(this.$control, {
      attributes: true,
      attributeOldValue: true,
      attributeFilter: [Attr.Data.Tn.Required],
    });

    this.updateRequired();
    this.syncValidityFromGroupClass();
  }

  initObservers() {
    this.fieldRequiredChangedObserver = new MutationObserver(() => {
      this.updateRequired();
    });

    this.groupObserver = new MutationObserver(() => {
      this.syncValidityFromGroupClass();
    });
  }

  syncValidityFromGroupClass() {
    const isValid = !this.$group.classList.contains('has-error');

    this.setValidity(isValid);
  }

  updateRequired() {
    const isRequired = getIsRequired(this);

    const { $control } = this;

    if (isRequired) {
      $control.setAttribute(Attr.Required, true);
    } else {
      $control.removeAttribute(Attr.Required);
    }

    $control.removeAttribute(Attr.Data.Tn.Required);
  }

  findHelpBlock() {
    const $el = dom.findFrom(this.$group, '.help-block');

    if (!$el.id) {
      $el.id = getHelpBlockId(this.name);
    }

    return $el;
  }

  getShortName(formKey) {
    return this.name.slice(formKey.length + 1)
      .replace(/_Checkbox|_Textarea|_Text|_Radio$/, '');
  }

  setValidity(isValid) {
    const { $control } = this;

    $control.setAttribute(Attr.Aria.Invalid, !isValid);

    if (isValid) {
      const helpBlockId = getHelpBlockId(this.name);

      const val = ($control.getAttribute(Attr.Aria.DescribedBy) || '')
        .replace(helpBlockId, '')
        .trim();

      if (val) {
        $control.setAttribute(Attr.Aria.DescribedBy, val);
      } else {
        $control.removeAttribute(Attr.Aria.DescribedBy);
      }
    } else {
      const $helpBlock = this.findHelpBlock();

      const helpBlockId = $helpBlock.id;

      const val = $control.getAttribute(Attr.Aria.DescribedBy) || '';

      if (val.indexOf(helpBlockId) === -1) {
        $control.setAttribute(Attr.Aria.DescribedBy, `${helpBlockId} ${val}`.trim());
      }
    }
  }
}

export default BridgeFormControl;
